/**
 * Typed DeCA API client.
 *
 * A thin layer over `openapi-fetch`, which is itself a thin layer over `fetch`: paths,
 * params, bodies and responses are all checked against `deca-openapi.yaml`, and a
 * contract change surfaces as a TypeScript error rather than a runtime surprise.
 *
 * Two conventions are baked in deliberately:
 *
 *  - **Relative URLs.** `baseUrl` defaults to `''`, so every call is same-origin
 *    (`/api/v1/...`). Vite proxies `/api` in development; Caddy serves the portal and the
 *    API from one origin in production. There is no CORS in the design and no absolute
 *    host compiled into the bundle.
 *  - **Errors are thrown, not returned.** `openapi-fetch` returns `{ data, error }`;
 *    TanStack Query wants a rejected promise. `unwrap()` bridges the two and gives every
 *    failure a single type — see `errors.ts`.
 */

import createFetchClient, { type Middleware } from 'openapi-fetch'
import type { paths } from './schema'
import { DecaApiError, isProblem, toApiError } from './errors.ts'

export interface DecaClientOptions {
  /**
   * Origin to call. Leave unset for same-origin `/api/...`, which is what the portal
   * should do. Set it only for a script or a test pointing at another environment.
   */
  baseUrl?: string
  /**
   * Called before every request. Return the portal JWT, or `null` when signed out.
   * A function rather than a value so the token can rotate without rebuilding the client.
   */
  getToken?: () => string | null | undefined | Promise<string | null | undefined>
  /**
   * Machine-to-machine key (`X-API-Key`) for TMS/ERP callers. Never ship this in the
   * portal bundle — the portal authenticates as a user, not as an organisation.
   */
  apiKey?: string
  /**
   * The client company a platform role is currently acting inside, or `null` for none. Sent as
   * `X-Organisation-Id`, which the server honours **only** for a principal with platform reach and
   * ignores for everyone else — so a stale value cannot widen a tenant user's reach, and cannot 403
   * them either.
   *
   * A function rather than a value because the portal's organisation context changes while the
   * client lives, exactly as the token does.
   */
  organisationId?: () => string | null | undefined
  /** UI locale for `Accept-Language`; the server localises problem titles with it. */
  language?: () => string
  /**
   * Invoked on any 401. Wire it to "clear session and route to /login". It fires for
   * expiry and revocation alike, so it must be idempotent.
   */
  onUnauthorized?: (error: DecaApiError) => void
  /** Escape hatch for tests. */
  fetch?: typeof globalThis.fetch
}

export type DecaClient = ReturnType<typeof createDecaClient>

export function createDecaClient(options: DecaClientOptions = {}) {
  const {
    baseUrl = '',
    getToken,
    apiKey,
    organisationId,
    language,
    onUnauthorized,
    fetch: fetchImpl,
  } = options

  const client = createFetchClient<paths>({
    baseUrl,
    ...(fetchImpl ? { fetch: fetchImpl } : {}),
  })

  const auth: Middleware = {
    async onRequest({ request }) {
      if (getToken) {
        const token = await getToken()
        if (token) request.headers.set('Authorization', `Bearer ${token}`)
      }
      if (apiKey) request.headers.set('X-API-Key', apiKey)
      if (organisationId) {
        const org = organisationId()
        if (org) request.headers.set('X-Organisation-Id', org)
      }
      if (language) request.headers.set('Accept-Language', language())
      return request
    },
    async onResponse({ response }) {
      if (response.status === 401 && onUnauthorized) {
        // Read a clone: the caller still needs the original body.
        let problem: unknown
        try {
          problem = await response.clone().json()
        } catch {
          /* a 401 with no body is still a 401 */
        }
        onUnauthorized(toApiError(problem, response))
      }
      return response
    },
  }

  client.use(auth)
  return client
}

/**
 * Turns an `openapi-fetch` result into a value or a thrown `DecaApiError`.
 *
 *   const deca = await unwrap(client.GET('/api/v1/decas/{decaId}', {
 *     params: { path: { decaId } },
 *   }))
 *
 * Reach for the raw `{ data, error }` form instead when a failure is an expected branch
 * rather than an exception — a 404 on the roadside lookup, for instance, is a result.
 */
export async function unwrap<T, E>(
  promise: Promise<{ data?: T; error?: E; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise
  if (error !== undefined && error !== null) throw toApiError(error, response)
  if (!response.ok) throw toApiError(undefined, response)
  return data as T
}

/**
 * Same, but returns the `ETag` alongside the body.
 *
 * The contract requires `If-Match` on every operation that mutates one existing document
 * (generate, modify, supersede, complete, void, draft edit), and only `GET /decas/{id}`
 * hands out an `ETag`. So a read must carry its version forward to the next write —
 * otherwise two traffic managers editing one DeCA silently overwrite each other, which on
 * a legal document is the failure that matters.
 */
export async function unwrapWithETag<T, E>(
  promise: Promise<{ data?: T; error?: E; response: Response }>,
): Promise<{ data: T; etag: string | null }> {
  const { data, error, response } = await promise
  if (error !== undefined && error !== null) throw toApiError(error, response)
  if (!response.ok) throw toApiError(undefined, response)
  return { data: data as T, etag: response.headers.get('etag') }
}

/**
 * Header object for a conditional write. Goes in `params.header`, which is where the
 * generated types expect it:
 *
 *   await client.POST('/api/v1/decas/{decaId}/generate', {
 *     params: { path: { decaId }, header: ifMatch(etag) },
 *   })
 *
 * Throws when there is no ETag rather than quietly omitting the header. The operations
 * that need it declare it **required**, so a missing one is a bug in the calling screen —
 * it means a write is about to go out with no idea what it is overwriting. Better to fail
 * in development than to lose a colleague's edit to a legal document in production.
 */
export function ifMatch(etag: string | null | undefined): { 'If-Match': string } {
  if (!etag) {
    throw new Error(
      'If-Match is required by this operation, but no ETag was supplied. Read the document ' +
        'first (GET /api/v1/decas/{decaId} via unwrapWithETag) and pass the ETag it returned.',
    )
  }
  return { 'If-Match': etag }
}

/**
 * Fetches a generated PDF as a `Blob`, for an in-portal preview or a save-as.
 *
 * Note this is *not* the BOE download path. The QR encodes `/d/{token}`, which must
 * download with no login and no intermediate page (BOE Third.3–4); this endpoint is the
 * authenticated portal equivalent and is what the year of post-window access uses.
 */
export async function fetchDecaPdf(
  decaId: string,
  options: DecaClientOptions & { version?: number } = {},
): Promise<Blob> {
  const { baseUrl = '', getToken, language, version } = options
  const token = getToken ? await getToken() : null
  const query = version == null ? '' : `?version=${encodeURIComponent(version)}`

  const response = await (options.fetch ?? globalThis.fetch)(
    `${baseUrl}/api/v1/decas/${encodeURIComponent(decaId)}/pdf${query}`,
    {
      headers: {
        Accept: 'application/pdf',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(language ? { 'Accept-Language': language() } : {}),
      },
    },
  )

  if (!response.ok) {
    let problem: unknown
    try {
      problem = await response.json()
    } catch {
      /* an error page that is not problem+json */
    }
    throw isProblem(problem) ? new DecaApiError(problem, response) : toApiError(problem, response)
  }
  return response.blob()
}
